import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { dashboardApi } from '@/services/dashboardApi';

const DashboardContext = createContext(null);

export function DashboardProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('dashboard_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      // Valor corrupto o almacenamiento bloqueado: se trata como sin sesión
      try { sessionStorage.removeItem('dashboard_user'); } catch { /* sin acceso */ }
      return null;
    }
  });
  const [membership, setMembership] = useState(null);
  // 'idle' | 'loading' | 'ready' | 'error' — permite distinguir "aún cargando"
  // de "no tiene membresía" (lo necesitan las secciones restringidas por plan).
  const [membershipStatus, setMembershipStatus] = useState('idle');

  const refreshMembership = useCallback(async () => {
    if (!user) return;
    setMembershipStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    try {
      const data = await dashboardApi.myMembership();
      setMembership(data);
      setMembershipStatus('ready');
    } catch (err) {
      setMembershipStatus('error');
      // Silencioso: si falla el fetch de membresía no bloqueamos el dashboard
      console.error('Failed to load membership:', err);
    }
  }, [user]);

  // Carga inicial cuando hay sesión
  useEffect(() => {
    if (user) refreshMembership();
    else { setMembership(null); setMembershipStatus('idle'); }
  }, [user, refreshMembership]);

  const login = useCallback(async (email, password) => {
    try {
      const data = await dashboardApi.login(email, password);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('dashboard_token');
    sessionStorage.removeItem('dashboard_user');
    setUser(null);
    setMembership(null);
    setMembershipStatus('idle');
  }, []);

  return (
    <DashboardContext.Provider value={{
      user,
      login,
      logout,
      isAdmin: user?.role === 'admin',
      membership,
      membershipStatus,
      refreshMembership,
    }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be inside DashboardProvider');
  return ctx;
}
